// Pure helpers for message rendering: timestamp formatting, grouping,
// and a deliberately small inline markdown parser. Everything here is
// side-effect-free and unit-tested separately so the Timeline and
// MessageBubble components can stay focused on layout.

import type { MessageMention } from "~/lib/markdown-render";
import { formatDateInZone } from "@nurion/datetime/format-date-in-zone";

import {
  MEETING_ACTIVE_STATE_TYPE,
  type TimelineEvent,
  type TimelineEventContent,
} from "~/lib/matrix";
import { LOCAL_ZONE } from "~/lib/relative-time";

/* ── Timestamps ───────────────────────────────────────────────── */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Short relative/absolute formatter the brief pins:
 * - "just now" under a minute
 * - "Nm ago" under an hour
 * - otherwise "MMM d, h:mm AM/PM" (e.g. "Apr 5, 3:45 PM")
 *
 * Locale is fixed to `en-US` because the brief mentions concrete forms
 * ("2m ago", "Apr 5, 3:45 PM"). Swap to `undefined` once the app
 * exposes a language setting.
 */
export function formatMessageTime(ts: number, now: number = Date.now()): string {
  const diff = now - ts;
  if (diff < MINUTE_MS) return "just now";
  if (diff < HOUR_MS) {
    const minutes = Math.max(1, Math.floor(diff / MINUTE_MS));
    return `${minutes}m ago`;
  }
  return formatDateInZone(
    new Date(ts).toISOString(),
    LOCAL_ZONE,
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
    "en-US",
  );
}

/** Clock time for a row that already sits under its day separator. */
export function formatTimeOfDay(ts: number): string {
  return formatDateInZone(
    new Date(ts).toISOString(),
    LOCAL_ZONE,
    { hour: "numeric", minute: "2-digit" },
    "en-US",
  );
}

/** Date-separator label: "Today", "Yesterday", or "Thursday, 22 April". */
export function formatDateSeparator(ts: number, now: number = Date.now()): string {
  const then = new Date(ts);
  const today = new Date(now);
  const yesterday = new Date(now - 24 * HOUR_MS);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(then, today)) return "Today";
  if (sameDay(then, yesterday)) return "Yesterday";
  return formatDateInZone(
    then.toISOString(),
    LOCAL_ZONE,
    { weekday: "long", day: "numeric", month: "long" },
    "en-US",
  );
}

/** Two timestamps on the same calendar day. */
export function isSameDay(aTs: number, bTs: number): boolean {
  const a = new Date(aTs);
  const b = new Date(bTs);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/* ── Grouping ─────────────────────────────────────────────────── */

export const GROUP_WINDOW_MS = 5 * MINUTE_MS;

export type GroupPosition = "first" | "middle" | "last" | "single";

export function isStateEvent(event: TimelineEvent): boolean {
  return (
    event.type === "m.room.member" ||
    event.type === "m.room.name" ||
    event.type === "m.room.topic" ||
    event.type === "m.room.power_levels" ||
    event.type === "m.room.tombstone" ||
    event.type === "m.room.canonical_alias" ||
    event.type === "m.room.history_visibility" ||
    event.type === "m.room.join_rules" ||
    event.type === "m.room.guest_access" ||
    event.type === "m.room.avatar" ||
    event.type === "m.room.create" ||
    event.type === "m.space.child" ||
    event.type === "m.space.parent" ||
    event.type === "com.nurion.channel_type" ||
    event.type === "com.nurion.archived" ||
    event.type === "m.state" ||
    event.type === "m.location" ||
    event.type === "m.redacted" ||
    event.type === "m.unsupported"
  );
}

export function isUndecryptableTimelineEvent(event: TimelineEvent): boolean {
  return event.type === "m.room.encrypted" || event.content.type === "m.undecryptable";
}

/**
 * Grouping rule from the brief: same sender, within 5 minutes, no
 * intervening state event. This returns the group position for each
 * event in a pre-sorted ascending list.
 */
export function computeGroupPositions(events: TimelineEvent[]): GroupPosition[] {
  const positions: GroupPosition[] = new Array(events.length).fill("single");
  for (let i = 0; i < events.length; i += 1) {
    const current = events[i];
    if (!current || isStateEvent(current) || isUndecryptableTimelineEvent(current)) {
      positions[i] = "single";
      continue;
    }
    const prev = i > 0 ? events[i - 1] : null;
    const next = i < events.length - 1 ? events[i + 1] : null;
    const sameAsPrev =
      !!prev &&
      !isStateEvent(prev) &&
      !isUndecryptableTimelineEvent(prev) &&
      prev.sender.id === current.sender.id &&
      current.ts - prev.ts <= GROUP_WINDOW_MS;
    const sameAsNext =
      !!next &&
      !isStateEvent(next) &&
      !isUndecryptableTimelineEvent(next) &&
      next.sender.id === current.sender.id &&
      next.ts - current.ts <= GROUP_WINDOW_MS;
    if (!sameAsPrev && sameAsNext) positions[i] = "first";
    else if (sameAsPrev && sameAsNext) positions[i] = "middle";
    else if (sameAsPrev && !sameAsNext) positions[i] = "last";
    else positions[i] = "single";
  }
  return positions;
}

/* ── Content helpers ──────────────────────────────────────────── */

const MENTION_ANCHOR_RE = /<a\s+href="https:\/\/matrix\.to\/#\/(@[^"/?]+)"[^>]*>([^<]+)<\/a>/g;
const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

/** User pills the sender's client linked in `formatted_body`, as display text + user id. */
export function messageMentions(content: TimelineEventBodyContent): MessageMention[] {
  const source =
    "newContent" in content && content.newContent && "formattedBody" in content.newContent
      ? content.newContent
      : content;
  const html = "formattedBody" in source ? source.formattedBody : null;
  if (!html) return [];
  const out: MessageMention[] = [];
  for (const match of html.matchAll(MENTION_ANCHOR_RE)) {
    const text = (match[2] ?? "").replace(/&(amp|lt|gt|quot|#39);/g, (e) => HTML_ENTITIES[e] ?? e);
    const userId = decodeURIComponent(match[1] ?? "");
    if (text.trim()) out.push({ text, userId });
  }
  return out;
}

/** UI copy for events whose envelope failed to decrypt. Imported by
 *  any caller that needs to render or preview the row. */
export const UNDECRYPTABLE_LABEL = "Unable to decrypt";

/** UI copy for `m.room.message` events whose msgtype the UI doesn't
 *  render. Imported by callers that need to preview the row. */
export const UNSUPPORTED_LABEL = "Unsupported message";

type TimelineEventBodyContent = Extract<TimelineEventContent, { body: string }>;

/**
 * A room upgrade tombstones the old room and names its successor in
 * `m.room.tombstone.replacementRoom`; return that id, or null.
 */
export function roomTombstoneReplacement(event: TimelineEvent): string | null {
  if (event.type !== "m.room.tombstone") return null;
  if (!("replacementRoom" in event.content)) return null;
  return event.content.replacementRoom ?? null;
}

export function replacementBodyOverride(content: TimelineEventBodyContent): string | null {
  if (!("newContent" in content)) return null;
  const replacement = content.newContent;
  return replacement && "body" in replacement ? replacement.body : null;
}

export function replacementBody(content: TimelineEventBodyContent): string {
  return replacementBodyOverride(content) ?? content.body;
}

/**
 * Human label for a state event so the row has something meaningful to
 * render. The brief says "single-line compact format" — this stays
 * neutral and declarative.
 */
export function stateEventLabel(
  event: TimelineEvent,
  opts: { viewerId?: string | null; redactorName?: string | null } = {},
): string | null {
  const name = event.sender.displayName || event.sender.id;
  switch (event.content.type) {
    case "m.room.member": {
      if (event.content.membership === null) return `${name} updated their membership`;
      const subject = event.content.displayName ?? event.content.prevContent?.displayName;
      const reason =
        typeof event.content.reason === "string" && event.content.reason.length > 0
          ? `: ${event.content.reason}`
          : "";
      switch (event.content.membership) {
        case "join":
          return `${subject ?? name} joined the room`;
        case "invite":
          return `${subject ?? name} was invited`;
        case "knock":
          return `${subject ?? name} knocked on the room`;
        case "leave": {
          // An unban arrives as a leave whose previous membership was a ban.
          if (event.content.prevContent?.membership === "ban") {
            return subject
              ? `${name} unbanned ${subject}${reason}`
              : `${name} unbanned a member${reason}`;
          }
          // A kick is a leave issued by someone other than the member; the sender is
          // never named as the one leaving.
          if (subject && subject !== name) return `${name} removed ${subject}${reason}`;
          if (subject) return `${subject} left the room`;
          return `${name} changed a room membership${reason}`;
        }
        case "ban":
          return subject
            ? `${name} banned ${subject}${reason}`
            : `${name} banned a member${reason}`;
      }
      return `${subject ?? name} updated their membership`;
    }
    case "m.room.name":
      // Guillemets read cleanly even when the new name itself contains
      // a straight double quote — straight quotes inside straight
      // quotes produce the "..."fine"" artefact a reviewer flagged.
      return `${name} changed the room name to \u00ab${event.content.name}\u00bb`;
    case "m.room.topic":
      return `${name} changed the topic to \u00ab${event.content.topic}\u00bb`;
    case "m.room.power_levels": {
      const users = event.content.users ?? {};
      const prevUsers = event.content.prevUsers ?? {};
      const changedUserIds = new Set([...Object.keys(users), ...Object.keys(prevUsers)]);
      const changed = [...changedUserIds].filter((userId) => users[userId] !== prevUsers[userId]);
      if (changed.length === 1) {
        const userId = changed[0] ?? "Someone";
        const level = users[userId];
        return level === undefined
          ? `${userId} was removed from explicit power levels`
          : `${userId} power level changed to ${level}`;
      }
      return changed.length > 1
        ? `${changed.length} users changed power levels`
        : `${name} changed power levels`;
    }
    case "m.room.tombstone":
      return event.content.replacementRoom
        ? `${name} tombstoned the room: ${event.content.replacementRoom}`
        : `${name} tombstoned the room${event.content.body ? `: ${event.content.body}` : ""}`;
    case "m.room.canonical_alias":
      return event.content.alias
        ? `${name} changed the room alias to ${event.content.alias}`
        : `${name} changed the room aliases`;
    case "m.room.history_visibility":
      return event.content.historyVisibility
        ? `${name} changed history visibility to ${event.content.historyVisibility}`
        : `${name} changed history visibility`;
    case "m.room.join_rules":
      return event.content.joinRule
        ? `${name} changed join rules to ${event.content.joinRule}`
        : `${name} changed join rules`;
    case "m.room.guest_access":
      return event.content.guestAccess
        ? `${name} changed guest access to ${event.content.guestAccess}`
        : `${name} changed guest access`;
    case "m.room.avatar":
      return `${name} changed the room avatar`;
    case "m.room.create":
      return event.content.roomType === "m.space"
        ? `${name} created the space`
        : `${name} created the room`;
    case "m.space.child":
      return `${name} changed the space children`;
    case "m.space.parent":
      return `${name} changed the space parents`;
    case "com.nurion.channel_type":
      return `${name} changed the channel type`;
    case "com.nurion.archived":
      return `${name} changed the archive state`;
    case "m.state": {
      if (event.content.eventType !== MEETING_ACTIVE_STATE_TYPE) {
        return event.content.eventType;
      }
      const payload = event.content.content;
      const active =
        typeof payload === "object" &&
        payload !== null &&
        (payload as { active?: unknown }).active === true;
      return active ? `${name} started a meeting` : `${name} ended the meeting`;
    }
    case "m.location":
      return event.content.description || event.content.body || `${name} shared a location`;
    case "m.redacted":
      return redactedRowLabel({
        redactedBy: event.content.redactedBy,
        viewerId: opts.viewerId,
        redactorName: opts.redactorName,
      });
    case "m.unsupported":
      return UNSUPPORTED_LABEL;
    default:
      return null;
  }
}

export function messageContentBody(content: TimelineEventContent): string | null {
  const replacement = "newContent" in content ? content.newContent : undefined;
  if (replacement) {
    const replacementBody = messageContentBody(replacement);
    if (replacementBody !== null) return replacementBody;
  }

  switch (content.type) {
    case "m.text":
    case "m.notice":
    case "m.emote":
    case "m.image":
    case "m.video":
    case "m.file":
    case "m.audio":
    case "m.location":
      return content.body;
    default:
      return null;
  }
}

function mxidLocalPart(mxid: string): string {
  const bare = mxid.startsWith("@") ? mxid.slice(1) : mxid;
  const colon = bare.indexOf(":");
  return colon >= 0 ? bare.slice(0, colon) : bare;
}

function capitalizeFirst(value: string): string {
  return value ? `${value[0]?.toUpperCase()}${value.slice(1)}` : value;
}

export function redactedRowLabel(input: {
  redactedBy?: string | null;
  viewerId?: string | null;
  redactorName?: string | null;
}): string {
  const by = input.redactedBy?.trim();
  if (!by) return "Message removed";
  if (input.viewerId && by === input.viewerId) return "You deleted this message";
  const display = input.redactorName?.trim();
  const who = display && display.length > 0 ? display : capitalizeFirst(mxidLocalPart(by));
  return `Message deleted by ${who}`;
}

/* ── URL extraction ───────────────────────────────────────────── */

/**
 * Hard cap on link previews per message. Matrix messages can legally
 * embed dozens of URLs; rendering a preview card for each is visually
 * hostile and would swamp the timeline with server requests. Three is
 * the number the ticket pins — it carries the "most messages have at
 * most one or two links" case without paying the cost of the pathological
 * one.
 */
export const MAX_LINK_PREVIEWS = 3;

/**
 * Extract HTTP(S) URLs from `body`, preserving first-occurrence order
 * and deduplicating. The regex deliberately mirrors the inline
 * markdown parser (`/^https?:\/\/[^\s<>`"']+/`) so a URL the parser
 * renders as a link always generates a preview — the two surfaces
 * disagreeing would produce an "underlined link with no card" or
 * "card with no underline" inconsistency the eye catches immediately.
 *
 * Trailing sentence punctuation (`. , ! ? ; :`) is stripped for the
 * same reason as the inline parser: "see https://x.tech." is prose
 * with a link, not a link to `https://x.tech.`. Parenthesised URLs
 * end in `)` legitimately ("…/Function_(math)") and are left alone.
 *
 * The cap is `MAX_LINK_PREVIEWS`; URLs beyond that are silently
 * dropped so the timeline renders a predictable upper bound.
 */
export function extractUrls(body: string): string[] {
  if (typeof body !== "string" || body.length === 0) return [];
  const pattern = /https?:\/\/[^\s<>`"']+/g;
  const out: string[] = [];
  const seen = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(body)) !== null) {
    let url = match[0];
    url = trimTrailingPunctuation(url);
    // A URL reduced to just the scheme is never a real link — skip
    // rather than emit a preview request for `https://`. The guard
    // checks "scheme + ://" (e.g. `https://`, 8 chars) and NOTHING
    // else; `http://x` (8 chars, valid) passes because we compare
    // against the scheme-prefix length of the URL itself.
    const schemePrefix = url.startsWith("https://") ? "https://" : "http://";
    if (url.length <= schemePrefix.length) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    out.push(url);
    if (out.length >= MAX_LINK_PREVIEWS) break;
  }
  return out;
}

/**
 * Strip trailing sentence punctuation AND an unmatched closing paren.
 * Matches the common prose cases:
 *   "check https://nurion.tech."   → "https://nurion.tech"
 *   "(see https://nurion.tech)"    → "https://nurion.tech"
 *   "Wiki: https://x.org/Function_(math)" → "https://x.org/Function_(math)"
 *
 * Parens that balance across the URL are kept (Wikipedia-style
 * URLs with parenthetical titles are a real class of input); a
 * trailing `)` with no matching `(` is almost always part of the
 * surrounding prose.
 */
function trimTrailingPunctuation(url: string): string {
  let result = url;
  // First: plain sentence punctuation. Loop until clean so `...` and
  // `?!` both collapse.
  while (result.length > 0 && /[.,!?;:]$/.test(result)) {
    result = result.slice(0, -1);
  }
  // Then: trailing `)` that isn't balanced by a preceding `(`.
  while (result.endsWith(")")) {
    const opens = (result.match(/\(/g) ?? []).length;
    const closes = (result.match(/\)/g) ?? []).length;
    if (opens >= closes) break;
    result = result.slice(0, -1);
    // Sentence punctuation may now appear directly before the `)`:
    // e.g. "(see https://x.tech.)" → strip `.` again after the paren.
    while (result.length > 0 && /[.,!?;:]$/.test(result)) {
      result = result.slice(0, -1);
    }
  }
  return result;
}

/** Human-readable file size for `m.file` rows. */
export function formatFileSize(bytes: number | undefined): string | undefined {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return undefined;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const REPLY_FALLBACK_HEAD_RE = /^> <@[^>\s]+>/;
const MATRIX_TO_LINK_RE = /\[([^\]]+)\]\(https:\/\/matrix\.to\/#\/[^)\s]+\)/g;

/** One-line preview of a plain body: no reply fallback quote, matrix.to links as their label. */
export function plainPreview(body: string): string {
  let lines = body.split("\n");
  if (REPLY_FALLBACK_HEAD_RE.test(lines[0] ?? "")) {
    const firstOwn = lines.findIndex((line) => !line.startsWith(">"));
    lines = firstOwn < 0 ? [] : lines.slice(firstOwn);
  }
  return lines.join(" ").replace(MATRIX_TO_LINK_RE, "$1").replace(/\s+/g, " ").trim();
}
